import Api from '../axios/api';
import { CompanyResponse } from './company.services';
import { AxiosRequestConfig } from 'axios';

export interface TruckResponse {
	count: number;
	items: DriverDto[];
}
interface PostTruckResponse {
	data: DriverDto;
}

interface DeleteTruckResponse {
	success: boolean;
}

interface ResponseTruckListResponse {
	data: TruckResponse;
}

export interface DriverDto {
	id: number;
	name: string;
	type: string;
	truckModel: string;
	truckYear: number;
	truckColor: string;
	truckNumber: string;
	truckBrand: string;
	truckPhoto: string;
	licensePlates: string;
	emissionType: string;
	driverFullName: string;
	driverAvatar: string;
	driverEmail: string;
	driverPhone: string;
	companyId: number;
	code: string;
	uid: string;
	status: string;
	createdAt: string;
	updatedAt: string;
	truckPayload: number;
	lastAddress?: string;
	qrCode?: string;
	qrPdf?: string;
	qrCodeUrl?: string;
	qrPdfUrl?: string;
	rating?: string;
	lastLocation?: {
		type: string;
		coordinates: number[];
	};
	lastSeenAt?: string;
	isOnline?: boolean;
	company?: CompanyResponse;
}

export interface IssuesDto {
	comment: string;
	createdAt: string;
	driverData: DriverDto | null;
	extensionId: number;
	id: number;
	oldDriver: string;
	oldDriverData: DriverDto;
	reason: string;
	status: string;
	transporterComment: string;
	updatedAt: string;
}

export enum DriverIssuesEnum {
	CREATED = 'created',
	IGNORE = 'ignore',
	RESOLVE = 'resolve',
}

/** Where and when the driver pressed an action button in the app. */
export interface DriverActionLocationDto {
	lat: number;
	long: number;
	/** when the driver pressed the button, epoch ms */
	recordedAt?: number;
	/** capture time of the GPS fix used for lat/long, epoch ms */
	fixAt?: number;
	timeZone?: string;
}

export interface DriverLoadDetailsDto {
	comments: string;
	wight: number;
	documents: string[];
	location?: DriverActionLocationDto;
	uploads?: { name: string; uploadedBy?: string; uploadedAt?: string }[];
}

export interface TruckDto {
	id: number;
	orderId: number;
	truckType: string;
	quantity: number;
	loadedQuantity?: number;
	payload: number;
	deliveryFrom: string;
	deliveryTo: string;
	priceForTransporter?: number;
	priceForTransporterWithVat?: number;
	transportVat?: number;
	priceForProducer?: number;
	priceForProducerWithVat?: number;
	createdAt: string;
	updatedAt: string;
	deliveryTimeFrom: string;
	deliveryTimeTo: string;
	referenceId?: string;
	loadDetails: DriverLoadDetailsDto;
	unloadDetails: DriverLoadDetailsDto;
	distance: number;
	driver: DriverDto;
	orderStatus: string;
	producerLoadDetails: DriverLoadDetailsDto;
	issues: IssuesDto[];
	route?: TruckRouteDto;
	telematics?: TruckTelematicsDto;
	/** Transport pricing v3 — cost per ton without platform margin (transporter). */
	transportCostPerTon?: number;
	/** Transport pricing v3 — price per ton with platform margin. */
	transportPricePerTon?: number;
	transportPricingMode?: 'DAILY_RATE' | 'PER_TON_KM' | 'LEGACY';
	transportZoneId?: string;
}

export interface TruckTelematicsDto {
	position: { lat: number; lng: number };
	speedKmh?: number;
	recordedAt: string;
	idleSince: string | null;
	trail: [number, number][];
	/** fix time (epoch ms) of each trail point */
	trailAt?: number[];
	/** client side only: smoothed driving speed of the live points */
	avgSpeedKmh?: number;
	/** last check-in of the driver app, moving or not (ISO) */
	lastSeenAt?: string;
	/** driver app online now */
	isOnline?: boolean;
	/** address of the last fix */
	address?: string;
}

export interface TruckRouteDto {
	shape: string;
	distance: number;
	trafficTime: number;
	/** what the truck drove between loading and unloading, JSON [[lat, lng], ...] */
	drivenShape?: string;
}

export interface CreateTruckParams {
	name: string;
	type: string;
	truckModel?: string;
	truckYear?: number;
	truckColor?: string;
	truckNumber: string;
	truckBrand?: string;
	truckPhoto: string;
	licensePlates: string;
	emissionType?: string;
	driverFullName: string;
	driverEmail: string;
	driverAvatar: string;
	driverPhone: string;
}

export interface FleetQueryParams {
	limit?: number;
	offset?: number;
	ids?: string[];
	status?: string;
	companyId?: number;
}

export interface UpdateTruck {
	name?: string;
	type?: string;
	truckModel?: string;
	truckYear?: number;
	truckColor?: string;
	truckNumber?: string;
	truckPhoto?: string;
	truckBrand?: string;
	licensePlates?: string;
	emissionType?: string;
	driverFullName?: string;
	driverAvatar?: string;
	driverEmail?: string;
	driverPhone?: string;
	status?: string;
	lastAddress?: string;
	lat?: number;
	long?: number;
}

export interface FleetBaseDto {
	id: number;
	createdBy: string;
	companyId: number;
	radiusDistance: number;
	address: string;
	location: {
		type: string;
		coordinates: number[];
	};
	createdAt: string;
	updatedAt: string;
	countryCode?: string;
}

interface FleetBaseResponse {
	data: FleetBaseDto;
}

interface GetFleetBasesResponse {
	data: {
		count: number;
		items: FleetBaseDto[];
	};
}

export interface UpdateFleetBase {
	radiusDistance: number;
	address: string;
	lat: number;
	long: number;
}

export interface ResolveIssuesBody {
	assigneeId: string;
	orderAcceptedForDriver: boolean;
}

export interface GetFleetBasesParams {
	limit: number;
	offset: number;
	ids?: number[];
	companyIds?: number[];
}

export class TruckService {
	static createTruck(body: CreateTruckParams): Promise<PostTruckResponse> {
		return Api.post(`/v1/transporter/fleets`, {
			...body,
		});
	}

	static allocateTruck(
		orderId: number,
		truckUid: string,
	): Promise<PostTruckResponse> {
		return Api.post(`/v1/transporter/orders/${orderId}/assign`, {
			truckUid,
		});
	}

	static getTrucks(
		query?: FleetQueryParams,
		target = 'transporter',
	): Promise<ResponseTruckListResponse> {
		return Api.get(`/v1/${target}/fleets`, {
			params: query,
		});
	}

	static deleteTruck(id: number): Promise<DeleteTruckResponse> {
		return Api.delete(`/v1/transporter/fleets/${id}`);
	}

	static editTruck(id: number, body: UpdateTruck): Promise<PostTruckResponse> {
		return Api.patch(`/v1/transporter/fleets/${id}`, {
			...body,
		});
	}

	static generateQr(
		id: number,
		target = 'transporter',
	): Promise<PostTruckResponse> {
		const lang = localStorage.getItem('lang') || 'pl';
		return Api.post(`/v1/${target}/fleets/generate-qr`, { id, lang });
	}

	static getMyFleetBase(): Promise<FleetBaseResponse> {
		return Api.get('/v1/transporter/fleet-bases');
	}

	static getFleetBases(
		params: GetFleetBasesParams,
	): Promise<GetFleetBasesResponse> {
		const config: AxiosRequestConfig = {
			params: { ...params },
		};
		return Api.get('/v1/admin/fleet-bases', config);
	}

	static updateFleetBase(body: UpdateFleetBase): Promise<FleetBaseResponse> {
		return Api.post('/v1/transporter/fleet-bases', {
			...body,
		});
	}

	static updateFleetBaseById(
		companyId: string,
		body: UpdateFleetBase,
	): Promise<FleetBaseResponse> {
		return Api.patch(`/v1/admin/fleet-bases/${companyId}`, {
			...body,
		});
	}

	static ignoreIssue(
		orderId: number,
		issueId: number,
		comment: string,
	): Promise<PostTruckResponse> {
		return Api.post(
			`/v1/transporter/orders/${orderId}/issue/${issueId}/ignore`,
			{ comment },
		);
	}

	static resolveIssues(
		orderId: number,
		issueId: number,
		body: ResolveIssuesBody,
	): Promise<PostTruckResponse> {
		return Api.post(
			`/v1/transporter/orders/${orderId}/issue/${issueId}/resolve`,
			{ ...body },
		);
	}
}
